import React, { useState } from 'react';
import { useOps } from '../context/OpsContext';
import { useNavigate, Link } from 'react-router-dom';
import { Search, Plus, Mail, Phone, Edit2, Download, Upload, Eye, Pencil, FileDown } from 'lucide-react';
import ImportModal from '../components/ImportModal';
import ExportButton from '../components/ExportButton';
import AddLeadModal from '../components/AddLeadModal';
import './Leads.css';

const Leads = () => {
    const { leads = [], addLead, handleTemplateImport, loadMoreLeads, hasMoreLeads, totalLeads } = useOps();
    const [searchTerm, setSearchTerm] = useState('');
    const [isImportOpen, setIsImportOpen] = useState(false);
    const [isAddOpen, setIsAddOpen] = useState(false);
    const navigate = useNavigate();

    const filtered = (leads || []).filter(l =>
        (l.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (l.phone || '').includes(searchTerm)
    );

    const exportColumns = [
        { header: 'Name', accessor: l => l.name },
        { header: 'Phone', accessor: l => l.phone },
        { header: 'Email', accessor: l => l.email },
        { header: 'Status', accessor: l => l.status },
        { header: 'Source', accessor: l => l.source },
        { header: 'Interest', accessor: l => l.interest },
        { header: 'Date', accessor: l => new Date(l.date).toLocaleDateString() },
    ];

    return (
        <div className="page-container">
            <div className="page-header">
                <div>
                    <h2 className="page-title">Sales Pipeline</h2>
                    <p className="page-subtitle">Track prospects and conversion opportunities</p>
                </div>
                <div className="flex gap-2">
                    <button
                        className="btn btn-primary flex items-center gap-2"
                        onClick={() => setIsAddOpen(true)}
                    >
                        <Plus size={18} /> Add Lead
                    </button>
                    <button
                        className="btn btn-secondary flex items-center gap-2"
                        onClick={() => setIsImportOpen(true)}
                    >
                        <Upload size={18} /> Bulk Import
                    </button>
                    <ExportButton
                        data={filtered}
                        columns={exportColumns}
                        fileName="leads_export"
                        className="btn btn-outline flex items-center gap-2"
                    />
                </div>
            </div>

            {/* Filters */}
            <div className="filters-bar">
                <div className="search-input-wrapper">
                    <Search size={18} className="search-icon" />
                    <input
                        type="text"
                        placeholder="Search by prospect name or mobile..."
                        className="search-input"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            {/* Table */}
            <div className="table-container card fade-in">
                <table className="data-table">
                    <thead>
                        <tr>
                            <th>Prospect Profile</th>
                            <th>Contact</th>
                            <th>Interest Area</th>
                            <th>Onboarding Date</th>
                            <th>Status</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.length === 0 ? (
                            <tr><td colSpan="6" className="text-center py-12 text-secondary">No prospects found matching your search.</td></tr>
                        ) : (
                            filtered.map((lead, i) => (
                                <tr key={lead.id || i}>
                                    <td className="font-medium">
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-lg bg-slate-100 text-slate-500 flex items-center justify-center font-bold text-xs">
                                                {lead.name ? lead.name[0] : 'L'}
                                            </div>
                                            <span className="text-sm font-bold text-slate-800">{lead.name}</span>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="contact-cell">
                                            <span className="text-xs font-medium">{lead.phone}</span>
                                            <span className="text-[10px] text-slate-400">{lead.email}</span>
                                        </div>
                                    </td>
                                    <td className="text-xs font-semibold text-slate-600">
                                        {lead.interest || 'General Inquiry'}
                                    </td>
                                    <td className="text-xs text-slate-400">
                                        {new Date(lead.date).toLocaleDateString()}
                                    </td>
                                    <td>
                                        <span className={`status-badge ${(lead.status || '').toLowerCase()}`}>
                                            {lead.status}
                                        </span>
                                    </td>
                                    <td>
                                        <div className="flex gap-1">
                                            <button className="icon-btn" title="Edit">
                                                <Pencil size={14} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
                {hasMoreLeads && (
                    <div className="flex justify-center p-4 border-t border-slate-100">
                        <button className="btn btn-secondary" onClick={loadMoreLeads}>
                            Load More ({leads.length} of {totalLeads})
                        </button>
                    </div>
                )}
            </div>

            <ImportModal
                isOpen={isImportOpen}
                onClose={() => setIsImportOpen(false)}
                onImport={handleTemplateImport}
                title="Import Leads"
                templateHeaders={['Name', 'Phone', 'Email', 'Source', 'Interest', 'Branch']}
                sampleRow={['John Doe', '0771234567', 'john@example.com', 'Facebook', 'Full Course', 'Harare']}
            />

            <AddLeadModal
                isOpen={isAddOpen}
                onClose={() => setIsAddOpen(false)}
            />
        </div>
    );
};

export default Leads;
